/**
 * useDronetagCloudLayer — polls the Dronetag Cloud API for drone positions
 * and renders them as entities on a Cesium viewer.
 *
 * Similar pattern to useFAAFacilityMap — takes a viewer, visibility toggle,
 * and API token. Polls every 10 seconds when visible.
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  Viewer as CesiumViewer,
  CustomDataSource,
  Cartesian3,
  Color,
  ConstantProperty,
  NearFarScalar,
  VerticalOrigin,
  HorizontalOrigin,
  LabelStyle,
} from 'cesium';
import { fetchDronetagTelemetry } from '../services/dronetagCloudApi';

const POLL_INTERVAL_MS = 10_000;
const DATASOURCE_NAME = 'dronetag-cloud';

// Color by operational state
const STATE_COLORS: Record<string, Color> = {
  airborne: Color.fromCssColorString('#ffb547'),     // accent/teal — flying
  ground: Color.fromCssColorString('#5fd5ff'),        // sky/blue — on ground
  hovering: Color.fromCssColorString('#FFD700'),      // gold — hovering
  emergency: Color.fromCssColorString('#e63b4d'),     // alert/red — emergency
};
const DEFAULT_COLOR = Color.fromCssColorString('#ffb547');

export function useDronetagCloudLayer(
  viewer: CesiumViewer | null,
  visible: boolean,
  apiToken: string | null | undefined,
  bbox?: { minLat: number; maxLat: number; minLng: number; maxLng: number },
) {
  const dsRef = useRef<CustomDataSource | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateEntities = useCallback(
    async (v: CesiumViewer) => {
      if (!apiToken) return;

      try {
        const positions = await fetchDronetagTelemetry(apiToken, bbox);
        let ds = dsRef.current;

        if (!ds) {
          ds = new CustomDataSource(DATASOURCE_NAME);
          dsRef.current = ds;
          v.dataSources.add(ds);
        }

        // Build set of current operation IDs
        const currentIds = new Set(positions.map((p) => p.operationId));

        // Remove entities that are no longer present
        const existing = ds.entities.values.slice();
        for (const entity of existing) {
          if (!currentIds.has(entity.id)) {
            ds.entities.removeById(entity.id);
          }
        }

        // Upsert entities
        for (const pos of positions) {
          const color = STATE_COLORS[pos.operationalState || ''] || DEFAULT_COLOR;
          const position = Cartesian3.fromDegrees(pos.longitude, pos.latitude, pos.altitudeMSL);
          const idLine = pos.registration || pos.serialNumber || pos.operationId.slice(0, 8);
          // Two-line label: identity + altitude in feet (matches the live map
          // and replay label format). Altitude hidden at 0 ft to avoid a
          // misleading label for ground-level / no-fix reports.
          const altFt = pos.altitudeMSL ? Math.round(pos.altitudeMSL * 3.28084) : 0;
          const label = altFt > 0 ? `${idLine}\n${altFt.toLocaleString()} ft` : idLine;

          const existing = ds.entities.getById(pos.operationId);
          if (existing) {
            // Update position
            existing.position = position as any;
            if (existing.point) {
              existing.point.color = color as any;
            }
            if (existing.label) {
              existing.label.text = new ConstantProperty(label) as any;
            }
          } else {
            // Create new entity
            ds.entities.add({
              id: pos.operationId,
              position,
              point: {
                pixelSize: 10,
                color,
                outlineColor: Color.WHITE,
                outlineWidth: 1,
                scaleByDistance: new NearFarScalar(1000, 1.2, 50000, 0.6),
              },
              label: {
                text: label,
                font: 'bold 11px "DM Sans", sans-serif',
                fillColor: Color.WHITE,
                style: LabelStyle.FILL,
                pixelOffset: new Cartesian3(0, -16, 0) as any,
                verticalOrigin: VerticalOrigin.BOTTOM,
                horizontalOrigin: HorizontalOrigin.CENTER,
                scaleByDistance: new NearFarScalar(1000, 1.0, 50000, 0.4),
                showBackground: false,
              },
            });
          }
        }
      } catch (err) {
        console.warn('[DronetagCloud] Fetch error:', err);
      }
    },
    [apiToken, bbox],
  );

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    if (!visible || !apiToken) {
      // Hide data source
      if (dsRef.current) {
        dsRef.current.show = false;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Show data source
    if (dsRef.current) {
      dsRef.current.show = true;
    }

    // Initial fetch
    updateEntities(viewer);

    // Start polling
    intervalRef.current = setInterval(() => {
      if (!viewer.isDestroyed()) {
        updateEntities(viewer);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [viewer, visible, apiToken, updateEntities]);

  // Cleanup data source on unmount
  useEffect(() => {
    return () => {
      if (dsRef.current && viewer && !viewer.isDestroyed()) {
        viewer.dataSources.remove(dsRef.current, true);
        dsRef.current = null;
      }
    };
  }, [viewer]);
}
